import React, { useState, useEffect, useMemo } from 'react';
import { useAuth, authAxios } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CalendarDays, BookOpen, FolderOpen, Image as ImageIcon, Search, Plus, 
  Cloud, CheckCircle2, MapPinned, LayoutGrid, NotebookPen, Filter, 
  ChevronRight, LogOut, User, Trash2, X, Upload, Sparkles, Lock, Pencil, FileDown
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const gradeBands = ["Preschool", "Elementary", "Middle School", "High School"];

const states = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware","Florida","Georgia",
  "Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky","Louisiana","Maine","Maryland",
  "Massachusetts","Michigan","Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada","New Hampshire","New Jersey",
  "New Mexico","New York","North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania","Rhode Island","South Carolina",
  "South Dakota","Tennessee","Texas","Utah","Vermont","Virginia","Washington","West Virginia","Wisconsin","Wyoming"
];

function SectionTitle({ icon: Icon, title, subtitle }) {
  return (
    <div className="flex items-start gap-3">
      <div className="rounded-xl bg-blue-600 text-white p-2 shadow-sm">
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <h2 className="text-xl md:text-2xl font-semibold tracking-tight text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h2>
        <p className="text-sm text-slate-500 mt-1" style={{ fontFamily: 'Figtree, sans-serif' }}>{subtitle}</p>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ lessons: 0, albums: 0, events: 0, standards_matched: 0 });
  const [subscription, setSubscription] = useState(null);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [isTrial, setIsTrial] = useState(false);
  const [daysRemaining, setDaysRemaining] = useState(0);
  const [subscriptionExpired, setSubscriptionExpired] = useState(false);
  const isAdmin = user?.role === 'admin';
  const canCreate = isAdmin || (hasSubscription && !subscriptionExpired);
  const [standards, setStandards] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedState, setSelectedState] = useState("North Carolina");
  const [selectedBand, setSelectedBand] = useState("Elementary");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Lesson form state
  const [lessonTitle, setLessonTitle] = useState("");
  const [objective, setObjective] = useState("");
  const [materials, setMaterials] = useState("");
  const [procedures, setProcedures] = useState("");
  const [assessment, setAssessment] = useState("");
  const [connectedStandards, setConnectedStandards] = useState([]);
  const [savingLesson, setSavingLesson] = useState(false);
  const [editingLessonId, setEditingLessonId] = useState(null);

  // Lesson search/filter state
  const [lessonSearchTerm, setLessonSearchTerm] = useState("");
  const [lessonFilterBand, setLessonFilterBand] = useState("all");

  // Album state
  const [showAlbumDialog, setShowAlbumDialog] = useState(false);
  const [newAlbumName, setNewAlbumName] = useState("");
  const [newAlbumDescription, setNewAlbumDescription] = useState("");
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [albumPhotos, setAlbumPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // Event state
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [newEventTitle, setNewEventTitle] = useState("");
  const [newEventDate, setNewEventDate] = useState("");
  const [newEventTime, setNewEventTime] = useState("");
  const [newEventGrade, setNewEventGrade] = useState("");

  const [connectedApps] = useState({ drive: true, calendar: false, albums: true });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchStandards();
  }, [selectedBand]);

  const fetchData = async () => {
    try {
      const [statsRes, lessonsRes, albumsRes, eventsRes, subRes] = await Promise.all([
        authAxios.get(`${API}/stats`),
        authAxios.get(`${API}/lessons`),
        authAxios.get(`${API}/albums`),
        authAxios.get(`${API}/events`),
        authAxios.get(`${API}/subscription/me`)
      ]);
      setStats(statsRes.data);
      setLessons(lessonsRes.data);
      setAlbums(albumsRes.data);
      setEvents(eventsRes.data);
      setSubscription(subRes.data.subscription);
      setHasSubscription(subRes.data.has_subscription);
      setIsTrial(subRes.data.is_trial || false);
      setDaysRemaining(subRes.data.days_remaining || 0);
      // Check if subscription/trial is expired
      const sub = subRes.data.subscription;
      const isExpired = sub && sub.status === 'expired';
      const noSub = !sub && user?.role !== 'admin';
      setSubscriptionExpired(isExpired || noSub);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const fetchStandards = async () => {
    try {
      const res = await authAxios.get(`${API}/standards?grade_band=${encodeURIComponent(selectedBand)}`);
      setStandards(res.data.standards || []);
    } catch (error) {
      console.error('Error fetching standards:', error);
    }
  };

  const filteredStates = useMemo(() => {
    return states.filter((state) => state.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [searchTerm]);

  const handleSaveLesson = async () => {
    if (!lessonTitle.trim()) return;
    setSavingLesson(true);
    try {
      if (editingLessonId) {
        // Update existing lesson
        await authAxios.put(`${API}/lessons/${editingLessonId}`, {
          title: lessonTitle,
          grade_band: selectedBand,
          state: selectedState,
          objective,
          materials,
          procedures,
          assessment,
          connected_standards: connectedStandards
        });
      } else {
        // Create new lesson
        await authAxios.post(`${API}/lessons`, {
          title: lessonTitle,
          grade_band: selectedBand,
          state: selectedState,
          objective,
          materials,
          procedures,
          assessment,
          connected_standards: connectedStandards
        });
      }
      
      // Reset form
      resetLessonForm();
      fetchData();
    } catch (error) {
      console.error('Error saving lesson:', error);
    } finally {
      setSavingLesson(false);
    }
  };

  const resetLessonForm = () => {
    setLessonTitle("");
    setObjective("");
    setMaterials("");
    setProcedures("");
    setAssessment("");
    setConnectedStandards([]);
    setEditingLessonId(null);
  };

  const handleEditLesson = (lesson) => {
    setLessonTitle(lesson.title || "");
    setObjective(lesson.objective || "");
    setMaterials(lesson.materials || "");
    setProcedures(lesson.procedures || "");
    setAssessment(lesson.assessment || "");
    setSelectedBand(lesson.grade_band || "Elementary");
    setSelectedState(lesson.state || "North Carolina");
    setConnectedStandards(lesson.connected_standards || []);
    setEditingLessonId(lesson.id);
  };

  const handleDeleteLesson = async (lessonId) => {
    try {
      await authAxios.delete(`${API}/lessons/${lessonId}`);
      if (editingLessonId === lessonId) {
        resetLessonForm();
      }
      fetchData();
    } catch (error) {
      console.error('Error deleting lesson:', error);
    }
  };

  const filteredLessons = useMemo(() => {
    return lessons.filter((lesson) => {
      const matchesSearch = !lessonSearchTerm || 
        lesson.title?.toLowerCase().includes(lessonSearchTerm.toLowerCase()) ||
        lesson.objective?.toLowerCase().includes(lessonSearchTerm.toLowerCase()) ||
        lesson.state?.toLowerCase().includes(lessonSearchTerm.toLowerCase());
      const matchesBand = lessonFilterBand === "all" || lesson.grade_band === lessonFilterBand;
      return matchesSearch && matchesBand;
    });
  }, [lessons, lessonSearchTerm, lessonFilterBand]);

  const handleCreateAlbum = async () => {
    if (!newAlbumName.trim()) return;
    try {
      await authAxios.post(`${API}/albums`, {
        name: newAlbumName,
        description: newAlbumDescription
      });
      setNewAlbumName("");
      setNewAlbumDescription("");
      setShowAlbumDialog(false);
      fetchData();
    } catch (error) {
      console.error('Error creating album:', error);
    }
  };

  const handleDeleteAlbum = async (albumId) => {
    try {
      await authAxios.delete(`${API}/albums/${albumId}`);
      if (selectedAlbum?.id === albumId) {
        setSelectedAlbum(null);
        setAlbumPhotos([]);
      }
      fetchData();
    } catch (error) {
      console.error('Error deleting album:', error);
    }
  };

  const handleOpenAlbum = async (album) => {
    setSelectedAlbum(album);
    try {
      const res = await authAxios.get(`${API}/albums/${album.id}/photos`);
      setAlbumPhotos(res.data);
    } catch (error) {
      console.error('Error fetching photos:', error);
    }
  };

  const handleUploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedAlbum) return;
    
    setUploadingPhoto(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await authAxios.post(`${API}/albums/${selectedAlbum.id}/photos`, formData, {
        
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      handleOpenAlbum(selectedAlbum);
      fetchData();
    } catch (error) {
      console.error('Error uploading photo:', error);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      await authAxios.delete(`${API}/photos/${photoId}`);
      if (selectedAlbum) {
        handleOpenAlbum(selectedAlbum);
      }
      fetchData();
    } catch (error) {
      console.error('Error deleting photo:', error);
    }
  };

  const handleCreateEvent = async () => {
    if (!newEventTitle.trim() || !newEventDate || !newEventTime) return;
    try {
      await authAxios.post(`${API}/events`, {
        title: newEventTitle,
        date: newEventDate,
        time: newEventTime,
        grade_band: newEventGrade
      });
      setNewEventTitle("");
      setNewEventDate("");
      setNewEventTime("");
      setNewEventGrade("");
      setShowEventDialog(false);
      fetchData();
    } catch (error) {
      console.error('Error creating event:', error);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      await authAxios.delete(`${API}/events/${eventId}`);
      fetchData();
    } catch (error) {
      console.error('Error deleting event:', error);
    }
  };

  const addStandardToLesson = (standardText) => {
    if (!connectedStandards.includes(standardText)) {
      setConnectedStandards([...connectedStandards, standardText]);
    }
  };

  const handleExportPDF = (lesson) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    
    const standards = (lesson.connected_standards || []).map(s => `<li>${s}</li>`).join('');
    const procedures = (lesson.procedures || '').replace(/\n/g, '<br>');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${lesson.title} - Lesson Plan</title>
        <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@600;700&family=Figtree:wght@400;500;600&display=swap" rel="stylesheet">
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Figtree', sans-serif; color: #0f172a; padding: 40px; max-width: 800px; margin: 0 auto; }
          h1 { font-family: 'Outfit', sans-serif; font-size: 28px; margin-bottom: 4px; }
          h2 { font-family: 'Outfit', sans-serif; font-size: 18px; color: #334155; margin: 24px 0 8px; border-bottom: 2px solid #e2e8f0; padding-bottom: 4px; }
          .meta { display: flex; gap: 16px; margin: 12px 0 20px; }
          .meta span { background: #f1f5f9; padding: 4px 12px; border-radius: 6px; font-size: 13px; color: #475569; }
          .section { margin-bottom: 8px; }
          .section p, .section li { font-size: 14px; line-height: 1.7; color: #334155; }
          ul { padding-left: 20px; }
          .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; border-bottom: 3px solid #2563eb; padding-bottom: 16px; }
          .brand { font-family: 'Outfit', sans-serif; font-size: 14px; color: #2563eb; font-weight: 600; }
          .footer { margin-top: 32px; padding-top: 16px; border-top: 1px solid #e2e8f0; font-size: 12px; color: #94a3b8; text-align: center; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <h1>${lesson.title}</h1>
            <div class="meta">
              <span>${lesson.grade_band}</span>
              <span>${lesson.state}</span>
            </div>
          </div>
          <div class="brand">Create(ED)</div>
        </div>
        
        <div class="section">
          <h2>Objective</h2>
          <p>${lesson.objective || 'Not specified'}</p>
        </div>
        
        <div class="section">
          <h2>Materials</h2>
          <p>${lesson.materials || 'Not specified'}</p>
        </div>
        
        <div class="section">
          <h2>Procedures</h2>
          <p>${procedures || 'Not specified'}</p>
        </div>
        
        <div class="section">
          <h2>Assessment</h2>
          <p>${lesson.assessment || 'Not specified'}</p>
        </div>
        
        ${standards ? `
        <div class="section">
          <h2>Connected Standards</h2>
          <ul>${standards}</ul>
        </div>
        ` : ''}
        
        <div class="footer">
          Created with Create(ED) &mdash; Art Teacher Workspace
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => { printWindow.print(); }, 500);
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-slate-900">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-blue-600">
              <NotebookPen className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Create(ED)</span>
          </div>
          <div className="flex items-center gap-4">
            {hasSubscription && isTrial ? (
              <div className="flex items-center gap-2">
                <Badge className="rounded-full bg-amber-100 text-amber-700 hover:bg-amber-100" data-testid="trial-badge">
                  Trial: {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} left
                </Badge>
                <Button
                  size="sm"
                  onClick={() => navigate('/subscription')}
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs px-3"
                  data-testid="upgrade-nav-button"
                >
                  Upgrade
                </Button>
              </div>
            ) : hasSubscription ? (
              <Badge className="rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-100" data-testid="subscription-active-badge">
                <CheckCircle2 className="h-3 w-3 mr-1" /> Subscribed
              </Badge>
            ) : (
              <Button
                size="sm"
                onClick={() => navigate('/subscription')}
                className="bg-amber-500 hover:bg-amber-600 text-white rounded-lg"
                data-testid="subscribe-nav-button"
              >
                <Sparkles className="h-4 w-4 mr-1" /> Subscribe
              </Button>
            )}
            <div className="flex items-center gap-2 text-slate-600">
              <User className="h-4 w-4" />
              <span className="text-sm" style={{ fontFamily: 'Figtree, sans-serif' }}>{user?.name}</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={logout}
              data-testid="logout-button"
              className="border-slate-300 text-slate-700 hover:bg-slate-50 rounded-lg"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign out
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto p-4 md:p-8 space-y-8">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="grid lg:grid-cols-[1.4fr_0.8fr] gap-6"
        >
          <Card className="rounded-xl border-slate-200 shadow-sm bg-white">
            <CardContent className="p-6 md:p-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div className="space-y-4 max-w-2xl">
                  <Badge className="rounded-full px-3 py-1 text-sm bg-blue-600 hover:bg-blue-700" data-testid="app-badge">Create(ED)</Badge>
                  <div>
                    <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Welcome back, {user?.name?.split(' ')[0]}!
                    </h1>
                    <p className="mt-3 text-base text-slate-600 leading-relaxed" style={{ fontFamily: 'Figtree, sans-serif' }}>
                      Plan lessons, align standards across all 50 states, organize student artwork, manage calendars, and connect your classroom workflow.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {gradeBands.map((band) => (
                      <Badge key={band} variant="secondary" className="rounded-full px-3 py-1 bg-slate-100 text-slate-700">{band}</Badge>
                    ))}
                  </div>
                </div>

                <Card className="w-full md:w-[320px] rounded-xl border-slate-200 shadow-sm">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2 text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      <Cloud className="h-5 w-5 text-blue-600" /> Connected Tools
                    </CardTitle>
                    <CardDescription className="text-slate-500" style={{ fontFamily: 'Figtree, sans-serif' }}>Keep classroom planning in sync.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
                      <div>
                        <p className="font-medium text-slate-800" style={{ fontFamily: 'Figtree, sans-serif' }}>Google Drive</p>
                        <p className="text-xs text-slate-500">Lesson files, handouts</p>
                      </div>
                      <Badge className="rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-100">{connectedApps.drive ? "Connected" : "Connect"}</Badge>
                    </div>
                    <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
                      <div>
                        <p className="font-medium text-slate-800" style={{ fontFamily: 'Figtree, sans-serif' }}>Photo Albums</p>
                        <p className="text-xs text-slate-500">Student work archives</p>
                      </div>
                      <Badge className="rounded-full bg-blue-100 text-blue-700 hover:bg-blue-100">{connectedApps.albums ? "Ready" : "Set Up"}</Badge>
                    </div>
                    <Button className="w-full rounded-lg bg-blue-600 hover:bg-blue-700 text-white" data-testid="manage-integrations-btn">
                      Manage Integrations
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-xl shadow-sm border-0 bg-slate-900 text-white overflow-hidden">
            <CardContent className="p-6 md:p-8 space-y-5">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-white/10"><LayoutGrid className="h-6 w-6" /></div>
                <div>
                  <h3 className="text-xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Today in Create(ED)</h3>
                  <p className="text-sm text-slate-300" style={{ fontFamily: 'Figtree, sans-serif' }}>Your quick snapshot</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  ["Lesson Plans", stats.lessons],
                  ["Albums", stats.albums],
                  ["Standards Matched", stats.standards_matched],
                  ["Upcoming Events", stats.events],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-xl bg-white/10 p-4" data-testid={`stat-${label.toLowerCase().replace(' ', '-')}`}>
                    <p className="text-sm text-slate-300" style={{ fontFamily: 'Figtree, sans-serif' }}>{label}</p>
                    <p className="text-2xl font-bold mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{value}</p>
                  </div>
                ))}
              </div>
              <div className="rounded-xl bg-white text-slate-900 p-4">
                <p className="text-sm font-medium text-slate-500" style={{ fontFamily: 'Figtree, sans-serif' }}>Featured workflow</p>
                <p className="text-lg font-semibold mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Standards-linked lesson builder</p>
                <p className="text-sm text-slate-600 mt-1" style={{ fontFamily: 'Figtree, sans-serif' }}>Choose a state, grade band, and standard, then drop it into a lesson plan.</p>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Tabs */}
        {/* Subscription Expired Banner */}
        {subscriptionExpired && !isAdmin && (
          <div className="p-5 bg-amber-50 border border-amber-200 rounded-xl flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-testid="subscription-expired-banner">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-amber-100">
                <Lock className="h-5 w-5 text-amber-700" />
              </div>
              <div>
                <h3 className="font-semibold text-amber-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Your subscription has expired
                </h3>
                <p className="text-sm text-amber-700 mt-1" style={{ fontFamily: 'Figtree, sans-serif' }}>
                  You can still view your existing lessons, albums, and events, but creating or editing content requires an active subscription.
                </p>
              </div>
            </div>
            <Button
              onClick={() => navigate('/subscription')}
              className="bg-amber-500 hover:bg-amber-600 text-white rounded-lg shrink-0"
              data-testid="subscribe-from-banner"
            >
              <Sparkles className="h-4 w-4 mr-2" /> Subscribe Now
            </Button>
          </div>
        )}

        <Tabs defaultValue="standards" className="space-y-6">
          <TabsList className="grid grid-cols-2 md:grid-cols-5 h-auto rounded-xl p-1 bg-slate-100">
            <TabsTrigger value="standards" className="rounded-lg py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-standards">Standards</TabsTrigger>
            <TabsTrigger value="lesson-planner" className="rounded-lg py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-lesson-planner">Lesson Planner</TabsTrigger>
            <TabsTrigger value="albums" className="rounded-lg py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-albums">Photo Albums</TabsTrigger>
            <TabsTrigger value="calendar" className="rounded-lg py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-calendar">Calendar</TabsTrigger>
            <TabsTrigger value="drive" className="rounded-lg py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-drive">Google Drive</TabsTrigger>
          </TabsList>

          {/* Standards Tab */}
          <TabsContent value="standards" className="space-y-6">
            <div className="grid xl:grid-cols-[0.95fr_1.05fr] gap-6">
              <Card className="rounded-xl shadow-sm border-slate-200">
                <CardHeader>
                  <SectionTitle icon={MapPinned} title="50-State Standards Hub" subtitle="Filter by state and grade band to connect learning goals." />
                </CardHeader>
                <CardContent className="space-y-5">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-slate-700" style={{ fontFamily: 'Figtree, sans-serif' }}>Search States</Label>
                      <div className="relative">
                        <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                        <Input 
                          value={searchTerm} 
                          onChange={(e) => setSearchTerm(e.target.value)} 
                          placeholder="Search all 50 states" 
                          className="pl-9 rounded-lg bg-slate-50 border-slate-200"
                          data-testid="search-states-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-slate-700" style={{ fontFamily: 'Figtree, sans-serif' }}>Grade Band</Label>
                      <Select value={selectedBand} onValueChange={setSelectedBand}>
                        <SelectTrigger className="rounded-lg bg-slate-50 border-slate-200" data-testid="grade-band-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {gradeBands.map((band) => <SelectItem key={band} value={band}>{band}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="rounded-xl border border-slate-200 p-4">
                    <p className="text-sm font-medium mb-3 text-slate-700" style={{ fontFamily: 'Figtree, sans-serif' }}>State Directory</p>
                    <div className="max-h-72 overflow-auto grid sm:grid-cols-2 gap-2 pr-1">
                      {filteredStates.map((state) => (
                        <button
                          key={state}
                          onClick={() => setSelectedState(state)}
                          data-testid={`state-${state.toLowerCase().replace(' ', '-')}`}
                          className={`text-left rounded-lg border px-3 py-2 transition-all text-sm ${
                            selectedState === state 
                              ? "bg-blue-600 text-white border-blue-600" 
                              : "border-slate-200 hover:bg-slate-50 text-slate-700"
                          }`}
                          style={{ fontFamily: 'Figtree, sans-serif' }}
                        >
                          {state}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-xl border border-dashed border-slate-300 p-4 bg-slate-50">
                    <p className="font-medium text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>Standards connection options</p>
                    <div className="mt-3 space-y-3 text-sm">
                      <div className="flex items-center gap-3">
                        <Checkbox checked id="state-art" />
                        <label htmlFor="state-art" className="text-slate-600" style={{ fontFamily: 'Figtree, sans-serif' }}>State visual arts standards</label>
                      </div>
                      <div className="flex items-center gap-3">
                        <Checkbox checked id="cross-curricular" />
                        <label htmlFor="cross-curricular" className="text-slate-600" style={{ fontFamily: 'Figtree, sans-serif' }}>Cross-curricular standards links</label>
                      </div>
                      <div className="flex items-center gap-3">
                        <Checkbox id="district-custom" />
                        <label htmlFor="district-custom" className="text-slate-600" style={{ fontFamily: 'Figtree, sans-serif' }}>District/custom standard sets</label>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="rounded-xl shadow-sm border-slate-200">
                <CardHeader>
                  <CardTitle className="text-2xl text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>{selectedState} • {selectedBand}</CardTitle>
                  <CardDescription className="text-slate-500" style={{ fontFamily: 'Figtree, sans-serif' }}>Standards alignment view for lesson mapping.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {standards.map((standard, index) => (
                    <motion.div
                      key={standard.id}
                      initial={{ opacity: 0, x: 8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="rounded-xl border border-slate-200 p-4 flex items-start justify-between gap-4"
                      data-testid={`standard-${standard.id}`}
                    >
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline" className="border-slate-300 text-slate-600">{standard.id}</Badge>
                          <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">{standard.category}</Badge>
                        </div>
                        <p className="font-medium text-slate-800" style={{ fontFamily: 'Figtree, sans-serif' }}>{standard.text}</p>
                        <p className="text-sm text-slate-500 mt-1" style={{ fontFamily: 'Figtree, sans-serif' }}>Map into lesson objectives and assessments.</p>
                      </div>
                      <Button 
                        variant="outline" 
                        className="rounded-lg shrink-0 border-slate-300 text-slate-700 hover:bg-slate-50"
                        onClick={() => addStandardToLesson(standard.text)}
                        data-testid={`add-standard-${standard.id}`}
                      >
                        Add to Lesson
                      </Button>
                    </motion.div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Lesson Planner Tab */}
          <TabsContent value="lesson-planner">
            <div className="grid xl:grid-cols-[1.2fr_0.8fr] gap-6">
              <Card className={`rounded-xl shadow-sm ${editingLessonId ? 'border-blue-300 border-2' : 'border-slate-200'}`}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <SectionTitle 
                      icon={editingLessonId ? Pencil : NotebookPen} 
                      title={editingLessonId ? "Editing Lesson Plan" : "Lesson Plan Studio"} 
                      subtitle={editingLessonId ? "Make changes and save to update your lesson." : "Design, save, and organize art lessons for any grade level."} 
                    />
                    {editingLessonId && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={resetLessonForm}
                        className="rounded-lg border-slate-300 text-slate-600 hover:bg-slate-50 shrink-0"
                        data-testid="cancel-edit-lesson"
                      >
                        <X className="h-4 w-4 mr-1" /> Cancel Edit
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-slate-700" style={{ fontFamily: 'Figtree, sans-serif' }}>Lesson Title</Label>
                      <Input 
                        value={lessonTitle} 
                        onChange={(e) => setLessonTitle(e.target.value)} 
                        placeholder="Pop Art Self-Portrait"
                        className="rounded-lg bg-slate-50 border-slate-200"
                        data-testid="lesson-title-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700" style={{ fontFamily: 'Figtree, sans-serif' }}>Grade Band</Label>
                        <Select value={selectedBand} onValueChange={setSelectedBand}>
                          <SelectTrigger className="rounded-lg bg-slate-50 border-slate-200" data-testid="lesson-grade-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {gradeBands.map((band) => <SelectItem key={band} value={band}>{band}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700" style={{ fontFamily: 'Figtree, sans-serif' }}>State</Label>
                        <Select value={selectedState} onValueChange={setSelectedState}>
                          <SelectTrigger className="rounded-lg bg-slate-50 border-slate-200" data-testid="lesson-state-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {states.map((state) => <SelectItem key={state} value={state}>{state}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700" style={{ fontFamily: 'Figtree, sans-serif' }}>Objective</Label>
                    <Textarea 
                      value={objective} 
                      onChange={(e) => setObjective(e.target.value)} 
                      placeholder="Students will create a self-portrait using bright color contrast..."
                      className="min-h-[100px] rounded-lg bg-slate-50 border-slate-200"
                      data-testid="lesson-objective-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700" style={{ fontFamily: 'Figtree, sans-serif' }}>Materials</Label>
                    <Textarea 
                      value={materials} 
                      onChange={(e) => setMaterials(e.target.value)}
                      placeholder="12x18 paper, mirrors, oil pastels, tempera cakes..."
                      className="min-h-[80px] rounded-lg bg-slate-50 border-slate-200"
                      data-testid="lesson-materials-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700" style={{ fontFamily: 'Figtree, sans-serif' }}>Procedures</Label>
                    <Textarea 
                      value={procedures} 
                      onChange={(e) => setProcedures(e.target.value)}
                      placeholder="1. Warm-up sketch&#10;2. Discuss pop art examples..."
                      className="min-h-[140px] rounded-lg bg-slate-50 border-slate-200"
                      data-testid="lesson-procedures-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700" style={{ fontFamily: 'Figtree, sans-serif' }}>Assessment</Label>
                    <Textarea 
                      value={assessment} 
                      onChange={(e) => setAssessment(e.target.value)}
                      placeholder="Checklist: craftsmanship, use of color..."
                      className="min-h-[80px] rounded-lg bg-slate-50 border-slate-200"
                      data-testid="lesson-assessment-input"
                    />
                  </div>

                  {connectedStandards.length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-slate-700" style={{ fontFamily: 'Figtree, sans-serif' }}>Connected Standards</Label>
                      <div className="flex flex-wrap gap-2">
                        {connectedStandards.map((std, idx) => (
                          <Badge key={idx} variant="secondary" className="rounded-full bg-blue-100 text-blue-700">
                            {std.slice(0, 40)}...
                            <button onClick={() => setConnectedStandards(connectedStandards.filter((_, i) => i !== idx))} className="ml-1">
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  <Button 
                    className={`w-full rounded-lg text-white ${canCreate ? (editingLessonId ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-blue-600 hover:bg-blue-700') : 'bg-slate-400 cursor-not-allowed'}`}
                    onClick={canCreate ? handleSaveLesson : () => navigate('/subscription')}
                    disabled={savingLesson || !lessonTitle.trim()}
                    data-testid="save-lesson-button"
                  >
                    {!canCreate 
                      ? <><Lock className="h-4 w-4 mr-2" /> Subscribe to Save</> 
                      : savingLesson 
                        ? 'Saving...' 
                        : editingLessonId 
                          ? <><CheckCircle2 className="h-4 w-4 mr-2" /> Update Lesson Plan</> 
                          : 'Save Lesson Plan'
                    }
                  </Button>

                  {editingLessonId && (
                    <Button 
                      variant="outline"
                      className="w-full rounded-lg border-slate-300 text-slate-700 hover:bg-slate-50"
                      onClick={() => {
                        const lesson = lessons.find(l => l.id === editingLessonId);
                        if (lesson) handleExportPDF(lesson);
                      }}
                      data-testid="pdf-current-lesson"
                    >
                      <FileDown className="h-4 w-4 mr-2" /> Save as PDF
                    </Button>
                  )}
                </CardContent>
              </Card>

              <div className="space-y-4">
                <Card className="rounded-xl shadow-sm border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-lg text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>My Lesson Plans</CardTitle>
                    <CardDescription className="text-slate-500" style={{ fontFamily: 'Figtree, sans-serif' }}>{lessons.length} lesson{lessons.length !== 1 ? 's' : ''} saved</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Search & Filter */}
                    <div className="space-y-2">
                      <div className="relative">
                        <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                        <Input
                          value={lessonSearchTerm}
                          onChange={(e) => setLessonSearchTerm(e.target.value)}
                          placeholder="Search lessons..."
                          className="pl-9 rounded-lg bg-slate-50 border-slate-200 text-sm h-9"
                          data-testid="lesson-search-input"
                        />
                      </div>
                      <Select value={lessonFilterBand} onValueChange={setLessonFilterBand}>
                        <SelectTrigger className="rounded-lg bg-slate-50 border-slate-200 text-sm h-9" data-testid="lesson-filter-band">
                          <SelectValue placeholder="Filter by grade band" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Grade Bands</SelectItem>
                          {gradeBands.map((band) => <SelectItem key={band} value={band}>{band}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Lessons List */}
                    <div className="max-h-[350px] overflow-auto space-y-2">
                      {filteredLessons.length === 0 ? (
                        <p className="text-sm text-slate-500 text-center py-4" style={{ fontFamily: 'Figtree, sans-serif' }}>
                          {lessons.length === 0 ? 'No lessons yet. Create your first one!' : 'No lessons match your search.'}
                        </p>
                      ) : (
                        filteredLessons.map((lesson) => (
                          <div 
                            key={lesson.id} 
                            className={`rounded-lg border p-3 transition-all cursor-pointer ${
                              editingLessonId === lesson.id 
                                ? 'border-blue-400 bg-blue-50' 
                                : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                            }`}
                            data-testid={`lesson-${lesson.id}`}
                            onClick={() => handleEditLesson(lesson)}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0 flex-1">
                                <p className="font-medium text-slate-800 truncate" style={{ fontFamily: 'Figtree, sans-serif' }}>{lesson.title}</p>
                                <div className="flex gap-2 mt-1">
                                  <Badge variant="secondary" className="text-xs bg-slate-100 text-slate-600">{lesson.grade_band}</Badge>
                                  <Badge variant="outline" className="text-xs border-slate-200 text-slate-500">{lesson.state}</Badge>
                                </div>
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-7 w-7 text-slate-400 hover:text-emerald-600"
                                  onClick={(e) => { e.stopPropagation(); handleExportPDF(lesson); }}
                                  data-testid={`pdf-lesson-${lesson.id}`}
                                  title="Save as PDF"
                                >
                                  <FileDown className="h-3.5 w-3.5" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-7 w-7 text-slate-400 hover:text-blue-600"
                                  onClick={(e) => { e.stopPropagation(); handleEditLesson(lesson); }}
                                  data-testid={`edit-lesson-${lesson.id}`}
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-7 w-7 text-slate-400 hover:text-red-500"
                                  onClick={(e) => { e.stopPropagation(); handleDeleteLesson(lesson.id); }}
                                  data-testid={`delete-lesson-${lesson.id}`}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Albums Tab */}
          <TabsContent value="albums">
            <Card className="rounded-xl shadow-sm border-slate-200">
              <CardHeader>
                <SectionTitle icon={ImageIcon} title="Photo Albums" subtitle="Organize student artwork, process photos, and exhibition collections." />
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
                  <div className="relative w-full md:max-w-md">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                    <Input placeholder="Search albums, projects, or students" className="pl-9 rounded-lg bg-slate-50 border-slate-200" />
                  </div>
                  <div className="flex gap-3">
                    <Button variant="outline" className="rounded-lg border-slate-300 text-slate-700">
                      <Filter className="h-4 w-4 mr-2" /> Filter
                    </Button>
                    <Button 
                      className={`rounded-lg text-white ${canCreate ? 'bg-blue-600 hover:bg-blue-700' : 'bg-slate-400 cursor-not-allowed'}`}
                      onClick={canCreate ? () => setShowAlbumDialog(true) : () => navigate('/subscription')}
                      data-testid="new-album-button"
                    >
                      {canCreate ? <><Plus className="h-4 w-4 mr-2" /> New Album</> : <><Lock className="h-4 w-4 mr-2" /> Subscribe</>}
                    </Button>
                  </div>
                </div>

                {selectedAlbum ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Button variant="outline" size="sm" onClick={() => { setSelectedAlbum(null); setAlbumPhotos([]); }} className="rounded-lg">
                          <ChevronRight className="h-4 w-4 rotate-180 mr-1" /> Back
                        </Button>
                        <h3 className="text-xl font-semibold text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>{selectedAlbum.name}</h3>
                      </div>
                      {canCreate ? (
                        <label className="cursor-pointer">
                          <input type="file" accept="image/*" onChange={handleUploadPhoto} className="hidden" />
                          <Button className="rounded-lg bg-blue-600 hover:bg-blue-700 text-white" disabled={uploadingPhoto} asChild>
                            <span><Upload className="h-4 w-4 mr-2" /> {uploadingPhoto ? 'Uploading...' : 'Upload Photo'}</span>
                          </Button>
                        </label>
                      ) : (
                        <Button className="rounded-lg bg-slate-400 text-white cursor-not-allowed" onClick={() => navigate('/subscription')}>
                          <Lock className="h-4 w-4 mr-2" /> Subscribe to Upload
                        </Button>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                      {albumPhotos.map((photo) => (
                        <div key={photo.id} className="relative group aspect-square rounded-lg overflow-hidden border border-slate-200" data-testid={`photo-${photo.id}`}>
                          <img 
                            src={`${process.env.REACT_APP_BACKEND_URL}${photo.url}`} 
                            alt={photo.original_name} 
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <Button 
                              variant="destructive" 
                              size="icon" 
                              className="h-8 w-8"
                              onClick={() => handleDeletePhoto(photo.id)}
                              data-testid={`delete-photo-${photo.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                      {albumPhotos.length === 0 && (
                        <div className="col-span-full text-center py-12 text-slate-500" style={{ fontFamily: 'Figtree, sans-serif' }}>
                          No photos yet. Upload your first one!
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
                    {albums.map((album) => (
                      <motion.div whileHover={{ y: -4 }} key={album.id}>
                        <Card className="rounded-xl h-full border-slate-200" data-testid={`album-${album.id}`}>
                          <CardContent className="p-5 space-y-4">
                            <div className="aspect-video rounded-lg bg-slate-100 overflow-hidden">
                              {album.cover_url ? (
                                <img 
                                  src={`${process.env.REACT_APP_BACKEND_URL}${album.cover_url}`} 
                                  alt={album.name} 
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-slate-400">
                                  <ImageIcon className="h-12 w-12" />
                                </div>
                              )}
                            </div>
                            <div>
                              <p className="font-semibold text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>{album.name}</p>
                              <p className="text-sm text-slate-500 mt-1" style={{ fontFamily: 'Figtree, sans-serif' }}>{album.photo_count} photos</p>
                            </div>
                            <div className="flex gap-2">
                              <Button 
                                variant="outline" 
                                className="flex-1 rounded-lg border-slate-300 text-slate-700"
                                onClick={() => handleOpenAlbum(album)}
                                data-testid={`open-album-${album.id}`}
                              >
                                Open
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="text-slate-400 hover:text-red-500"
                                onClick={() => handleDeleteAlbum(album.id)}
                                data-testid={`delete-album-${album.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    ))}
                    {albums.length === 0 && (
                      <div className="col-span-full text-center py-12 text-slate-500" style={{ fontFamily: 'Figtree, sans-serif' }}>
                        No albums yet. Create your first one!
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Calendar Tab */}
          <TabsContent value="calendar">
            <Card className="rounded-xl shadow-sm border-slate-200">
              <CardHeader>
                <SectionTitle icon={CalendarDays} title="Customizable Calendar" subtitle="Plan class rotations, deadlines, exhibits, and school art events." />
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex justify-end">
                  <Button 
                    className={`rounded-lg text-white ${canCreate ? 'bg-blue-600 hover:bg-blue-700' : 'bg-slate-400 cursor-not-allowed'}`}
                    onClick={canCreate ? () => setShowEventDialog(true) : () => navigate('/subscription')}
                    data-testid="new-event-button"
                  >
                    {canCreate ? <><Plus className="h-4 w-4 mr-2" /> New Event</> : <><Lock className="h-4 w-4 mr-2" /> Subscribe</>}
                  </Button>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {events.map((event) => (
                    <Card key={event.id} className="rounded-xl border-slate-200" data-testid={`event-${event.id}`}>
                      <CardContent className="p-4 space-y-3">
                        <div className="flex items-start justify-between">
                          <Badge className="rounded-full bg-blue-100 text-blue-700 hover:bg-blue-100">{event.date}</Badge>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-6 w-6 text-slate-400 hover:text-red-500"
                            onClick={() => handleDeleteEvent(event.id)}
                            data-testid={`delete-event-${event.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        <div>
                          <p className="font-semibold text-slate-800 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{event.title}</p>
                          <p className="text-sm text-slate-500 mt-1" style={{ fontFamily: 'Figtree, sans-serif' }}>{event.time}</p>
                          {event.grade_band && (
                            <Badge variant="secondary" className="mt-2 text-xs bg-slate-100 text-slate-600">{event.grade_band}</Badge>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {events.length === 0 && (
                    <div className="col-span-full text-center py-12 text-slate-500" style={{ fontFamily: 'Figtree, sans-serif' }}>
                      No events scheduled. Add your first one!
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Google Drive Tab */}
          <TabsContent value="drive">
            <Card className="rounded-xl shadow-sm border-slate-200">
              <CardHeader>
                <SectionTitle icon={FolderOpen} title="Google Drive Connection" subtitle="Centralize lesson files, images, rubrics, slides, and printable resources." />
              </CardHeader>
              <CardContent className="grid xl:grid-cols-[0.95fr_1.05fr] gap-6">
                <Card className="rounded-xl border-dashed border-slate-300">
                  <CardHeader>
                    <CardTitle className="text-lg text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>Drive Integration Panel</CardTitle>
                    <CardDescription className="text-slate-500" style={{ fontFamily: 'Figtree, sans-serif' }}>Designed for syncing folders, templates, and lesson assets.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4 text-sm">
                    {[
                      "Connect Create(ED) to Google Drive",
                      "Choose default lesson plan folder",
                      "Attach slides, docs, and images to lessons",
                      "Back up photo albums to Drive",
                      "Import templates from shared folders",
                    ].map((item) => (
                      <div key={item} className="rounded-lg border border-slate-200 p-3 flex items-center justify-between gap-3 hover:bg-slate-50 cursor-pointer transition-colors">
                        <span className="text-slate-700" style={{ fontFamily: 'Figtree, sans-serif' }}>{item}</span>
                        <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
                      </div>
                    ))}
                    <Button className="w-full rounded-lg bg-blue-600 hover:bg-blue-700 text-white" data-testid="connect-drive-button">
                      Connect Google Drive
                    </Button>
                  </CardContent>
                </Card>

                <Card className="rounded-xl border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-lg text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>Suggested File Structure</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    {[
                      "Create(ED) / Lesson Plans",
                      "Create(ED) / Standards References",
                      "Create(ED) / Student Photo Albums",
                      "Create(ED) / Art Show Materials",
                      "Create(ED) / Assessment Rubrics",
                    ].map((folder) => (
                      <div key={folder} className="rounded-lg bg-slate-50 border border-slate-200 p-3 flex items-center gap-3">
                        <FolderOpen className="h-4 w-4 text-amber-500" />
                        <span className="text-slate-700" style={{ fontFamily: 'Figtree, sans-serif' }}>{folder}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* New Album Dialog */}
      <Dialog open={showAlbumDialog} onOpenChange={setShowAlbumDialog}>
        <DialogContent className="rounded-xl">
          <DialogHeader>
            <DialogTitle className="text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>Create New Album</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-700" style={{ fontFamily: 'Figtree, sans-serif' }}>Album Name</Label>
              <Input 
                value={newAlbumName} 
                onChange={(e) => setNewAlbumName(e.target.value)} 
                placeholder="e.g., Kindergarten Clay Project"
                className="rounded-lg bg-slate-50 border-slate-200"
                data-testid="new-album-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700" style={{ fontFamily: 'Figtree, sans-serif' }}>Description (optional)</Label>
              <Textarea 
                value={newAlbumDescription} 
                onChange={(e) => setNewAlbumDescription(e.target.value)}
                placeholder="Add a description for this album..."
                className="rounded-lg bg-slate-50 border-slate-200"
                data-testid="new-album-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAlbumDialog(false)} className="rounded-lg">Cancel</Button>
            <Button 
              onClick={handleCreateAlbum} 
              className="rounded-lg bg-blue-600 hover:bg-blue-700 text-white"
              disabled={!newAlbumName.trim()}
              data-testid="create-album-submit"
            >
              Create Album
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Event Dialog */}
      <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
        <DialogContent className="rounded-xl">
          <DialogHeader>
            <DialogTitle className="text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>Add New Event</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-700" style={{ fontFamily: 'Figtree, sans-serif' }}>Event Title</Label>
              <Input 
                value={newEventTitle} 
                onChange={(e) => setNewEventTitle(e.target.value)} 
                placeholder="e.g., Grade 5 Art Show"
                className="rounded-lg bg-slate-50 border-slate-200"
                data-testid="new-event-title-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-700" style={{ fontFamily: 'Figtree, sans-serif' }}>Date</Label>
                <Input 
                  type="date" 
                  value={newEventDate} 
                  onChange={(e) => setNewEventDate(e.target.value)}
                  className="rounded-lg bg-slate-50 border-slate-200"
                  data-testid="new-event-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700" style={{ fontFamily: 'Figtree, sans-serif' }}>Time</Label>
                <Input 
                  type="time" 
                  value={newEventTime} 
                  onChange={(e) => setNewEventTime(e.target.value)}
                  className="rounded-lg bg-slate-50 border-slate-200"
                  data-testid="new-event-time-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700" style={{ fontFamily: 'Figtree, sans-serif' }}>Grade Band (optional)</Label>
              <Select value={newEventGrade || "all"} onValueChange={(val) => setNewEventGrade(val === "all" ? "" : val)}>
                <SelectTrigger className="rounded-lg bg-slate-50 border-slate-200" data-testid="new-event-grade-select">
                  <SelectValue placeholder="Select grade band" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Grades</SelectItem>
                  {gradeBands.map((band) => <SelectItem key={band} value={band}>{band}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEventDialog(false)} className="rounded-lg">Cancel</Button>
            <Button 
              onClick={handleCreateEvent} 
              className="rounded-lg bg-blue-600 hover:bg-blue-700 text-white"
              disabled={!newEventTitle.trim() || !newEventDate || !newEventTime}
              data-testid="create-event-submit"
            >
              Add Event
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
